/* ================================================================
   Structural Presets & Project Templates
   ----------------------------------------------------------------
   Tool2 exposes "新建从模板创建" and a set of side-structure archetypes
   (箱体 / 框架 / 折叠 etc). This module provides:

     - `STRUCTURAL_PRESETS`: per-side one-click structures
     - `PROJECT_TEMPLATES`: full-project starting points
     - `applyStructuralPreset` / `applyProjectTemplate`

   All mutations go through the immutable operations layer so the
   editor store's undo/redo and solve cache work unchanged.
   ================================================================ */

import type {
  ProjectModel, ProjectTemplate, LayerStyle, SideId, Side, Layer,
} from '../model/types';
import { createDefaultLayer, createDefaultProject } from '../model/defaults';

// --------------- Types ---------------

export interface StructuralPreset {
  id: string;
  name: string;
  description: string;
  /** Which sides this preset applies to. If omitted → all enabled sides. */
  targetSides?: SideId[];
  /** Layer recipe (top of stack first). */
  recipe: Array<{
    style: LayerStyle;
    direction: 0 | 1;
    thickness: number;
    color?: string;
    boardCount?: number;
    frameCount?: number[];
  }>;
}

// --------------- Catalog ---------------

export const STRUCTURAL_PRESETS: StructuralPreset[] = [
  {
    id: 'solid-panel-standard',
    name: '整板封闭',
    description: '单层整板，适合轻型普通封闭箱体',
    recipe: [
      { style: 1, direction: 0, thickness: 12, color: '#c8a96e' },
    ],
  },
  {
    id: 'board-strip-3',
    name: '3 根板条',
    description: '纵向 3 根板条，通用结构',
    recipe: [
      { style: 0, direction: 0, thickness: 18, boardCount: 3 },
    ],
  },
  {
    id: 'board-strip-5',
    name: '5 根板条',
    description: '纵向 5 根板条，托盘常用',
    recipe: [
      { style: 0, direction: 1, thickness: 20, boardCount: 5 },
    ],
  },
  {
    id: 'cross-grid-with-skin',
    name: '田字 + 蒙皮',
    description: '田字框架再蒙整板，承重强',
    recipe: [
      { style: 3, direction: 0, thickness: 18, frameCount: [2, 2] },
      { style: 1, direction: 0, thickness: 9 },
    ],
  },
  {
    id: 'heavy-beam',
    name: '重型梁结构',
    description: '底部厚板条 90mm + 顶部薄板条 20mm (托盘)',
    targetSides: ['pedestal', 'topCap'],
    recipe: [
      { style: 4, direction: 0, thickness: 90, boardCount: 3 },
    ],
  },
];

// --------------- Project-level templates ---------------

export const PROJECT_TEMPLATES: ProjectTemplate[] = [
  {
    id: 'template-export-crate',
    name: '出口熏蒸木箱',
    category: 'crate',
    description: '6 面封闭，熏蒸松木，符合 ISPM-15',
    scenarioTags: ['export', 'ispm-15', 'heat-treatment'],
    payload: {
      specification: {
        code: 'ISPM-15',
        name: 'ISPM-15 出口木质包装',
        category: 'heat-treatment',
        minBoardThickness: 15,
        minFrameThickness: 30,
        recommendedStackLayers: 3,
      },
      conditions: {
        stackLayers: 3,
        environment: 'export',
        transportMode: 'sea',
        humidity: 'humid',
        antiStatic: false,
        moistureBarrier: true,
      },
    },
  },
  {
    id: 'template-light-plywood-crate',
    name: '轻型胶合板箱',
    category: 'crate',
    description: '12mm 胶合板 6 面封闭，适合普通货',
    scenarioTags: ['domestic', 'light'],
    payload: {
      specification: {
        code: 'GB/T 4857',
        name: '国标普通包装',
        category: 'plywood',
        minBoardThickness: 9,
      },
      conditions: {
        stackLayers: 4,
        environment: 'indoor',
        transportMode: 'road',
        humidity: 'normal',
        antiStatic: false,
        moistureBarrier: false,
      },
    },
  },
  {
    id: 'template-heavy-skid-pallet',
    name: '重型托盘',
    category: 'pallet',
    description: '90mm 厚板条底座，承载 2000kg',
    scenarioTags: ['pallet', 'heavy-duty'],
    payload: {
      payloadKg: 2000,
    },
  },
];

// --------------- Appliers ---------------

/**
 * Replace the layer stack of a single side with the preset recipe.
 * Returns a brand-new Side; caller rebuilds the project.
 */
function buildSideFromPreset(side: Side, preset: StructuralPreset): Side {
  const newLayers: Layer[] = preset.recipe.map((r, idx) => {
    const layer = createDefaultLayer(side.sideId, idx, r.style, r.direction);
    layer.thickness = r.thickness;
    if (r.color) {
      layer.color = r.color;
      layer.styleSetting.materialcolor = r.color;
    }
    if (r.boardCount !== undefined) {
      layer.styleSetting.count.board = r.boardCount;
      if (layer.style === 0 || layer.style === 4) {
        layer.blockBoard.count = r.boardCount;
      }
    }
    if (r.frameCount !== undefined) {
      layer.styleSetting.count.frame = r.frameCount.slice();
    }
    return layer;
  });

  return { ...side, layers: newLayers };
}

export function applyStructuralPreset(
  project: ProjectModel,
  preset: StructuralPreset,
  sideIds?: SideId[],
): ProjectModel {
  const targets = sideIds && sideIds.length > 0
    ? new Set(sideIds)
    : preset.targetSides
      ? new Set(preset.targetSides)
      : new Set(project.sides.filter(s => s.enabled).map(s => s.sideId));

  const newSides = project.sides.map(side => {
    if (!targets.has(side.sideId)) return side;
    return buildSideFromPreset(side, preset);
  });

  // Reindex layers on affected sides
  for (const side of newSides) {
    side.layers.forEach((l, i) => {
      l.layerIndex = i;
      l.isBase = i === 0;
    });
  }

  return {
    ...project,
    sides: newSides,
    meta: { ...project.meta, updatedAt: new Date().toISOString() },
  };
}

/**
 * Merge a ProjectTemplate payload onto a fresh project of the given mode.
 * Undefined fields in the template fall back to the mode default.
 */
export function applyProjectTemplate(template: ProjectTemplate): ProjectModel {
  const base = createDefaultProject(template.category === 'pallet' ? 'pallet' : 'crate');
  const merged: ProjectModel = {
    ...base,
    ...template.payload,
    meta: {
      ...base.meta,
      name: template.name,
    },
    // sides from payload (if any) replace base
    sides: (template.payload.sides as any) ?? base.sides,
    innerSize: template.payload.innerSize ?? base.innerSize,
    outerSize: template.payload.outerSize ?? base.outerSize,
    goodsSize: template.payload.goodsSize ?? base.goodsSize,
    symmetry: template.payload.symmetry ?? base.symmetry,
  };
  return merged;
}
